import LoadingBox from 'components/Common/Loading/LoadingBox'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import Divider from 'LuxKit/Divider'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import {
  RoundedTable,
  ThingsToDoCard,
  TipCard,
} from './TripPlanDetailDrawer.styles'
import { useCallback, useMemo } from 'react'
import { useTripPlanDetailDrawerCopy } from './useTripPlanDetailDrawerCopy'
import type { ChatMapMarker } from 'tripPlanner/components/ChatMap/types'
import {
  useGetPlaceAreas,
  useGetPlaceBestTimeToVisit,
  useGetPlaceInsiderTips,
  useGetPlaceOverview,
  useGetPlaceWhatToLove,
} from 'tripPlanner/hooks/api/chat'
import TableBody from 'LuxKit/Tables/TableBody'
import TableBodyCell from 'LuxKit/Tables/TableBodyCell'
import TableBodyRow from 'LuxKit/Tables/TableBodyRow'
import TableHeader from 'LuxKit/Tables/TableHeader'
import TableHeaderCell from 'LuxKit/Tables/TableHeaderCell'
import TableHeaderRow from 'LuxKit/Tables/TableHeaderRow'
import DrawerDetailMap, { DrawerDetailMapFrame } from './DrawerDetailMap'

interface ThingsToDoSummary {
  count: number
  countLabel: string
  formattedLowestPrice: string | undefined
  isLoading: boolean
  isError: boolean
}

interface Props {
  googlePlaceId: string
  placeName: string
  countryName?: string
  onSwitchToThingsToDo: () => void
  onEnlargeMap?: () => void
  thingsToDoSummary: ThingsToDoSummary
  chatId?: number
}

function OverviewTab({
  googlePlaceId,
  placeName,
  countryName,
  onSwitchToThingsToDo,
  onEnlargeMap,
  thingsToDoSummary,
  chatId,
}: Props) {
  const {
    count: experiencesCount,
    countLabel: experiencesCountLabel,
    formattedLowestPrice: experiencesLowestPrice,
    isLoading: isExperiencesLoading,
    isError: isExperiencesError,
  } = thingsToDoSummary
  const labels = useTripPlanDetailDrawerCopy(countryName)
  const {
    data: overview,
    isLoading: isOverviewLoading,
    isError: isOverviewError,
    refetch: refetchOverview,
  } = useGetPlaceOverview(googlePlaceId, chatId)

  const {
    data: bestTimeToVisit,
    isLoading: isBestTimeLoading,
    isError: isBestTimeError,
    refetch: refetchBestTime,
  } = useGetPlaceBestTimeToVisit(googlePlaceId, chatId)

  const {
    data: whatToLove,
    isLoading: isWhatToLoveLoading,
    isError: isWhatToLoveError,
    refetch: refetchWhatToLove,
  } = useGetPlaceWhatToLove(googlePlaceId, chatId)

  const {
    data: areas,
    isLoading: isAreasLoading,
    isError: isAreasError,
    refetch: refetchAreas,
  } = useGetPlaceAreas(googlePlaceId, chatId)

  const {
    data: insiderTips,
    isLoading: isTipsLoading,
    isError: isTipsError,
    refetch: refetchTips,
  } = useGetPlaceInsiderTips(googlePlaceId, chatId)

  const isAllError = isOverviewError && isBestTimeError && isWhatToLoveError && isAreasError && isTipsError

  const handleRetry = useCallback(() => {
    refetchOverview()
    refetchBestTime()
    refetchWhatToLove()
    refetchAreas()
    refetchTips()
  }, [refetchOverview, refetchBestTime, refetchWhatToLove, refetchAreas, refetchTips])

  const mapMarkers = useMemo((): Array<ChatMapMarker> => {
    if (!overview) {
      return []
    }

    const markers: Array<ChatMapMarker> = [
      {
        markerKey: googlePlaceId,
        lat: overview.lat,
        lng: overview.lng,
        name: placeName,
        label: placeName,
        variant: 'primary',
      },
    ]

    if (areas?.areas) {
      areas.areas.forEach((area) => {
        if (area.lat !== undefined && area.lng !== undefined) {
          markers.push({
            markerKey: `area-${area.area}`,
            lat: area.lat,
            lng: area.lng,
            name: area.area,
            label: area.area,
            variant: 'secondary',
          })
        }
      })
    }

    return markers
  }, [overview, googlePlaceId, placeName, areas?.areas])

  return (
    <>
      {isAllError && (
        <Group direction="vertical" gap={8} horizontalAlign="center" verticalAlign="center">
          <BodyText variant="medium" colour="critical">
            {labels.failedToLoadText}
          </BodyText>
          <TextButton kind="secondary" size="small" onClick={handleRetry}>
            {labels.retryLabel}
          </TextButton>
        </Group>
      )}

      {/* Overview Section */}
      {isOverviewLoading && (
        <Group direction="vertical" gap={8}>
          <LoadingBox height={20} borderRadius="S" style={{ width: '100%' }} />
          <LoadingBox height={20} borderRadius="S" style={{ width: '90%' }} />
          <LoadingBox height={20} width={200} borderRadius="S" />
        </Group>
      )}
      {!isOverviewLoading && overview?.overview && (
        <Group direction="vertical" gap={8}>
          <BodyText variant="large" weight="bold">
            {overview.overview.tagline}
          </BodyText>
          <BodyText variant="medium">
            {overview.overview.details}
          </BodyText>
        </Group>
      )}

      {/* Things to Do CTA Card */}
      {isExperiencesLoading && (
        <LoadingBox height={80} borderRadius="M" style={{ width: '100%' }} />
      )}
      {!isExperiencesLoading && !isExperiencesError && experiencesCount > 0 && (
        <ThingsToDoCard
          size="medium"
          title={labels.thingsToDoTabLabel}
          titleWeight="bold"
          subtitle={experiencesCountLabel}
          startIcon={<LineTicketIcon colour="highlight-primary-dark" />}
          endIcon={<LineAngleRightIcon />}
          onClick={onSwitchToThingsToDo}
        >
          {experiencesLowestPrice && (
            <Group direction="vertical" gap={2} horizontalAlign="end" shrink={false}>
              <Caption variant="large" colour="light-foreground-alt">{labels.fromLabel}</Caption>
              <BodyText variant="large" weight="bold">{experiencesLowestPrice}</BodyText>
            </Group>
          )}
        </ThingsToDoCard>
      )}

      <Divider kind="primary" />

      {/* Best Time to Visit Section */}
      {isBestTimeLoading && (
        <Group direction="vertical" gap={16}>
          <LoadingBox height={24} width={160} borderRadius="S" />
          <LoadingBox height={20} borderRadius="S" style={{ width: '100%' }} />
          <LoadingBox height={150} borderRadius="L" style={{ width: '100%' }} />
        </Group>
      )}
      {!isBestTimeLoading && bestTimeToVisit?.bestTimeToVisit && (
        <Group direction="vertical" gap={16}>
          <Heading variant="heading5">{labels.bestTimeToVisitHeading}</Heading>
          <BodyText variant="medium">{bestTimeToVisit.bestTimeToVisit.summary}</BodyText>
          <RoundedTable>
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell width="33%">{labels.seasonHeader}</TableHeaderCell>
                <TableHeaderCell width="33%">{labels.weatherHeader}</TableHeaderCell>
                <TableHeaderCell width="33%">{labels.whyVisitHeader}</TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>
            <TableBody>
              {bestTimeToVisit.bestTimeToVisit.seasons.map((season) => (
                <TableBodyRow key={season.name}>
                  <TableBodyCell width="33%">
                    <BodyText variant="medium" weight="bold">{season.emoji} {season.name}</BodyText>
                    <Caption variant="large" colour="light-foreground-alt">{season.months}</Caption>
                  </TableBodyCell>
                  <TableBodyCell width="33%">
                    {labels.formatSeasonWeather(season.typicalMinTempC, season.typicalMaxTempC, season.weather)}
                  </TableBodyCell>
                  <TableBodyCell width="33%">{season.whyVisit}</TableBodyCell>
                </TableBodyRow>
              ))}
            </TableBody>
          </RoundedTable>
        </Group>
      )}

      <Divider kind="primary" />

      {/* What to Love Section */}
      {isWhatToLoveLoading && (
        <Group direction="vertical" gap={16}>
          <LoadingBox height={24} width={120} borderRadius="S" />
          <Group direction="vertical" gap={8}>
            <LoadingBox height={80} borderRadius="M" style={{ width: '100%' }} />
            <LoadingBox height={80} borderRadius="M" style={{ width: '100%' }} />
            <LoadingBox height={80} borderRadius="M" style={{ width: '100%' }} />
          </Group>
        </Group>
      )}
      {!isWhatToLoveLoading && whatToLove?.whatToLove && whatToLove.whatToLove.length > 0 && (
        <Group direction="vertical" gap={16}>
          <Heading variant="heading5">{labels.whatYoullLoveHeading}</Heading>
          <Group direction="vertical" gap={20}>
            {whatToLove.whatToLove.map((highlight) => (
              <Group key={highlight.item} direction="horizontal" gap={12} verticalAlign="center">
                <BodyText variant="large">{highlight.emoji}</BodyText>
                <Group direction="vertical" gap={4}>
                  <BodyText variant="medium" weight="bold">{highlight.item}</BodyText>
                  <BodyText variant="medium">{highlight.description}</BodyText>
                </Group>
              </Group>
            ))}
          </Group>
        </Group>
      )}

      {/* Areas Section */}
      {isAreasLoading && (
        <>
          <Divider kind="primary" />
          <Group direction="vertical" gap={16}>
            <LoadingBox height={24} width={140} borderRadius="S" />
            <Group direction="vertical" gap={8}>
              <LoadingBox height={80} borderRadius="M" style={{ width: '100%' }} />
              <LoadingBox height={80} borderRadius="M" style={{ width: '100%' }} />
            </Group>
          </Group>
        </>
      )}
      {!isAreasLoading && areas?.areas && areas.areas.length > 0 && (
        <>
          <Divider kind="primary" />
          <Group direction="vertical" gap={16}>
            <Heading variant="heading5">{labels.topAreasHeading}</Heading>
            <Group direction="vertical" gap={20}>
              {areas.areas.map((area, index) => (
                <Group key={area.area} direction="horizontal" gap={12} verticalAlign="center">
                  <Group direction="vertical" gap={4}>
                    <BodyText variant="medium" weight="bold">{labels.formatAreaName(index + 1, area.area)}</BodyText>
                    <BodyText variant="medium">{area.description}</BodyText>
                  </Group>
                </Group>
              ))}
            </Group>
          </Group>
        </>
      )}

      {/* Insider Tips Section */}
      {isTipsLoading && (
        <>
          <Divider kind="primary" />
          <Group direction="vertical" gap={16}>
            <LoadingBox height={24} width={120} borderRadius="S" />
            <Group direction="vertical" gap={8}>
              <LoadingBox height={80} borderRadius="M" style={{ width: '100%' }} />
              <LoadingBox height={80} borderRadius="M" style={{ width: '100%' }} />
            </Group>
          </Group>
        </>
      )}
      {!isTipsLoading && insiderTips?.insiderTips && insiderTips.insiderTips.length > 0 && (
        <>
          <Divider kind="primary" />
          <Group direction="vertical" gap={16}>
            <Heading variant="heading5">{labels.insiderTipsHeading}</Heading>
            <Group direction="vertical" gap={8}>
              {insiderTips.insiderTips.map((tip) => (
                <TipCard key={tip.tip}>
                  <Group direction="vertical" gap={4}>
                    <BodyText variant="medium" weight="bold">{tip.tip}</BodyText>
                    <BodyText variant="medium">{tip.description}</BodyText>
                  </Group>
                </TipCard>
              ))}
            </Group>
          </Group>
        </>
      )}

      <Divider kind="primary" />

      {/* Location Map */}
      <Heading variant="heading5">{labels.locationHeading}</Heading>

      {isOverviewLoading && (
        <DrawerDetailMapFrame>
          <LoadingBox floating borderRadius="L" />
        </DrawerDetailMapFrame>
      )}
      {!isOverviewLoading && overview && (
        <DrawerDetailMap
          markers={mapMarkers}
          initialCenterPlaceId={googlePlaceId}
          onEnlarge={onEnlargeMap}
        />
      )}
    </>
  )
}

export default OverviewTab
